import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { SEO } from '../components/SEO';

interface OvulationState {
  lastPeriod: string;
  cycleLength: string;
  periodLength: string;
}

function KalkulatorOvulacije() {
  const [values, setValues] = useState<OvulationState>({
    lastPeriod: '',
    cycleLength: '28',
    periodLength: '5'
  });

  const [results, setResults] = useState<{
    ovulationDate: Date;
    fertileWindowStart: Date;
    fertileWindowEnd: Date;
    nextPeriod: Date;
    currentPhase: string;
    daysUntilOvulation: number;
  } | null>(null);

  const calculateDates = () => {
    if (!values.lastPeriod) return;

    const lastPeriod = new Date(values.lastPeriod);
    const cycleLength = parseInt(values.cycleLength) || 28;
    
    // Calculate ovulation (typically 14 days before next period)
    const ovulationDate = new Date(lastPeriod);
    ovulationDate.setDate(ovulationDate.getDate() + cycleLength - 14);
    
    // Fertile window is typically 5 days before to 1 day after ovulation
    const fertileWindowStart = new Date(ovulationDate);
    fertileWindowStart.setDate(fertileWindowStart.getDate() - 5);
    
    const fertileWindowEnd = new Date(ovulationDate);
    fertileWindowEnd.setDate(fertileWindowEnd.getDate() + 1);
    
    // Next period
    const nextPeriod = new Date(lastPeriod);
    nextPeriod.setDate(nextPeriod.getDate() + cycleLength);

    // Calculate current phase and days until ovulation
    const today = new Date();
    let currentPhase = '';
    const daysUntilOvulation = Math.ceil((ovulationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (today >= fertileWindowStart && today <= fertileWindowEnd) {
      currentPhase = 'Plodno razdoblje';
    } else if (today < fertileWindowStart) {
      currentPhase = 'Folikularna faza';
    } else {
      currentPhase = 'Lutealna faza';
    }

    setResults({
      ovulationDate,
      fertileWindowStart,
      fertileWindowEnd,
      nextPeriod,
      currentPhase,
      daysUntilOvulation
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('hr-HR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleInputChange = (field: keyof OvulationState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Ovulacije",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte plodne dane i pratite menstrualni ciklus. Precizni izračuni za planiranje trudnoće.",
    "url": "https://online-kalkulator.com/kalkulator-ovulacije",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Ovulacije | Izračun Plodnih Dana"
        description="Izračunajte plodne dane i pratite menstrualni ciklus. Precizni izračuni za planiranje trudnoće."
        canonicalUrl="/kalkulator-ovulacije"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calendar className="w-8 h-8 text-pink-500" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Ovulacije</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prvi dan zadnje menstruacije
                  </label>
                  <input
                    type="date"
                    value={values.lastPeriod}
                    onChange={(e) => handleInputChange('lastPeriod', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duljina ciklusa (dani)
                  </label>
                  <input
                    type="number"
                    value={values.cycleLength}
                    onChange={(e) => handleInputChange('cycleLength', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="npr. 28"
                    min="21"
                    max="35"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trajanje menstruacije (dani)
                  </label>
                  <input
                    type="number"
                    value={values.periodLength}
                    onChange={(e) => handleInputChange('periodLength', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="npr. 5"
                    min="2"
                    max="8"
                  />
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateDates}
                className="w-full py-2 px-4 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-pink-50">
                    <p className="text-lg font-semibold text-pink-600">
                      {results.currentPhase}
                    </p>
                    {results.daysUntilOvulation > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        {results.daysUntilOvulation} dana do ovulacije
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Ovulacija:</span>
                      <span className="font-semibold">{formatDate(results.ovulationDate)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Plodni dani:</span>
                      <span className="font-semibold">
                        {formatDate(results.fertileWindowStart)} - {formatDate(results.fertileWindowEnd)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Sljedeća menstruacija:</span>
                      <span className="font-semibold">{formatDate(results.nextPeriod)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Ovulacije</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator ovulacije je koristan alat koji vam pomaže predvidjeti plodne dane i
                  pratiti menstrualni ciklus. Izračuni se temelje na prosječnom menstrualnom ciklusu
                  i mogu varirati od osobe do osobe.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faze Menstrualnog Ciklusa</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Folikularna faza</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Počinje prvim danom menstruacije</li>
                      <li>Traje do ovulacije</li>
                      <li>Folikuli u jajnicima sazrijevaju</li>
                      <li>Endometrij se zadebljava</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Ovulacija</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Obično se događa 14 dana prije sljedeće menstruacije</li>
                      <li>Oslobađanje jajne stanice</li>
                      <li>Najplodnije razdoblje ciklusa</li>
                      <li>Traje 24-36 sati</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Lutealna faza</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Počinje nakon ovulacije</li>
                      <li>Traje do sljedeće menstruacije</li>
                      <li>Priprema za moguću trudnoću</li>
                      <li>Relativno konstantna duljina od 14 dana</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite prvi dan zadnje menstruacije</li>
                  <li>Odaberite prosječnu duljinu ciklusa (najčešće 28 dana)</li>
                  <li>Unesite prosječno trajanje menstruacije</li>
                  <li>Kliknite "Izračunaj" za rezultate</li>
                  <li>Pratite prikazane datume i faze ciklusa</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Plodni Dani</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>5 dana prije ovulacije</li>
                  <li>Dan ovulacije</li>
                  <li>24 sata nakon ovulacije</li>
                  <li>Najveća vjerojatnost začeća je 2 dana prije ovulacije</li>
                  <li>Spermiji mogu preživjeti do 5 dana u reproduktivnom traktu</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su približni i mogu varirati</li>
                  <li>Svaka žena ima jedinstveni ciklus</li>
                  <li>Stres i drugi faktori mogu utjecati na ciklus</li>
                  <li>Za preciznije praćenje, vodite dnevnik ciklusa</li>
                  <li>Konzultirajte se s liječnikom za osobne savjete</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Znakovi Ovulacije</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Promjene u cervikalnoj sluzi</li>
                  <li>Blagi bolovi u donjem dijelu trbuha</li>
                  <li>Lagani porast bazalne temperature</li>
                  <li>Povećani libido</li>
                  <li>Osjetljivost grudi</li>
                  <li>Promjene raspoloženja</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorOvulacije;